/**
 * Analytics for card-number redaction (PCI). Events carry ids, roles and counts only —
 * never the text, its length, positions or digits, since PostHog is a third party.
 */
import { useMetricEvents } from '@/composables/useMetricEvents'

/** What the backend's `chd_redaction.py` writes in place of card data. */
const BACKEND_REDACTION_PLACEHOLDER = '[CHD-REDACTED]'

/** Messages already reported, so a refetch or reload does not count one redaction twice. */
const reportedMessageIds = new Set<string>()

/**
 * The frontend redacted card numbers out of a field's text.
 *
 * @param source - Which field did it, e.g. `chat_composer`.
 * @param matchCount - How many card numbers were replaced.
 */
export const trackFrontendRedaction = (source: string, matchCount: number) => {
  useMetricEvents().sendEvent('chd_redacted_frontend', {
    source,
    match_count: matchCount,
  })
}

/**
 * The backend redacted a message: its saved text carries the backend's placeholder.
 * Each message is reported once.
 */
export const trackBackendRedactions = (
  chatId: string,
  messages: ReadonlyArray<{
    id?: string
    role: 'assistant' | 'user'
    content?: string
  }>,
) => {
  const { sendEvent } = useMetricEvents()

  for (const { id, role, content } of messages) {
    if (!id || !content || reportedMessageIds.has(id)) continue

    const redactionCount =
      content.split(BACKEND_REDACTION_PLACEHOLDER).length - 1
    if (!redactionCount) continue

    reportedMessageIds.add(id)
    sendEvent('chd_redacted_by_backend', {
      chat_id: chatId,
      message_id: id,
      role,
      redaction_count: redactionCount,
    })
  }
}
